/**
 * useListPage Hook
 *
 * Reduces code duplication across list pages (Vendor, Customer, Category, Contact, etc.)
 * Encapsulates common patterns: pagination, filtering, sorting, selection, and table props.
 *
 * Design Principles:
 * - AHA: 4+ list pages with 80%+ duplication - abstraction is justified
 * - KISS: Simple API, entity-specific logic stays in components
 * - Open/Closed: Extensible for entity-specific needs via optional parameters
 *
 * Example usage:
 *
 * ```tsx
 * const {
 *   tableProps,
 *   isLoading,
 *   error,
 *   refresh,
 *   filters,
 *   setFilters,
 *   activeFilterCount,
 * } = useListPage({
 *   useTableHook: useVendorsTable,
 *   filters: vendorFilters,
 *   onFilterChange: (key, value) => {
 *     if (key === 'search') setSearch(value);
 *     if (key === 'categoryIds') setCategoryIds(value);
 *   },
 *   filterCountExclusions: ['isActive'], // Don't count isActive as a filter
 * });
 * ```
 */

import { useMemo } from 'react';
import type { TableProps } from 'antd';

/**
 * Base record type for table data - matches Ant Design's AnyObject pattern
 * @see https://ant.design/components/table/#components-table-demo-basic
 */
export type TableRecordType = Record<string, unknown>;

/**
 * Generic filter type - can be extended by specific entities
 */
export interface BaseFilters {
  search?: string;
  isActive?: boolean | null;
  [key: string]: unknown;
}

/**
 * List state triad (S0.3) — every list page derives one of these once,
 * and renders the standardized empty / zero-results / error components
 * from `@/components/common/TableContainer`.
 *
 * - 'loading'      initial or in-flight fetch (table hooks must implement the
 *                  `loading || params.length === 0` guard so the empty state
 *                  never flashes before the first fetch)
 * - 'error'        fetch failed → render error + retry
 * - 'empty'        fetched, zero rows, NO active search/filters → first-use
 *                  empty state (+ create CTA)
 * - 'zero-results' fetched, zero rows, WITH active search/filters → no-match
 *                  state (+ clear-filters CTA)
 * - 'data'         fetched, rows present
 */
export type ListState = 'loading' | 'error' | 'empty' | 'zero-results' | 'data';

/**
 * Result from table hooks (useVendorsTable, useCustomersTable, etc.)
 *
 * Contract (house rules):
 * - `isLoading` must include the pre-first-fetch guard: `loading || params.length === 0`
 * - the underlying fetch must use an `/api/`-prefixed static cacheKey when cached
 */
export interface TableHookResult<TRecord = TableRecordType> {
  tableProps: TableProps<TRecord>;
  isLoading: boolean;
  error?: Error | null;
  refresh: () => void;
}

/**
 * Options for useListPage hook
 */
export interface UseListPageOptions<TFilters extends object, TRecord = TableRecordType> {
  /**
   * The entity-specific table hook (e.g., useVendorsTable, useCustomersTable)
   * This hook handles the actual data fetching using useAntdTable
   */
  useTableHook: (filters: TFilters) => TableHookResult<TRecord>;

  /**
   * Current filter values from the store
   */
  filters: TFilters;

  /**
   * Optional: Fields to exclude from active filter count
   * Common exclusions: ['isActive'] - status toggle is not a filter
   */
  filterCountExclusions?: (keyof TFilters)[];

  /**
   * Optional: In compact mode, some filters might not count
   * (e.g., category filter in CategoryDetailPage)
   */
  compactMode?: boolean;

  /**
   * Optional: Additional fields to count specially
   * E.g., country field might be comma-separated string
   */
  customFilterCounters?: Partial<Record<keyof TFilters, (value: unknown) => number>>;
}

/**
 * Return type from useListPage hook
 */
export interface UseListPageReturn<TFilters extends object, TRecord = TableRecordType> {
  /**
   * Table props to spread directly onto <Table {...tableProps} />
   */
  tableProps: TableProps<TRecord>;

  /**
   * Loading state
   */
  isLoading: boolean;

  /**
   * Error state
   */
  error: Error | null;

  /**
   * Refresh function to re-fetch data
   */
  refresh: () => void;

  /**
   * Current filter values
   */
  filters: TFilters;

  /**
   * Count of active filters (excluding configured exclusions)
   * Used for filter badge count
   */
  activeFilterCount: number;

  /**
   * True when a search term or any counted filter is applied —
   * drives the empty vs zero-results split
   */
  isFiltered: boolean;

  /**
   * Derived list state triad (S0.3): loading / error / empty / zero-results / data
   */
  listState: ListState;
}

/**
 * Default filter count logic
 * Counts how many filters are actively applied
 */
function countActiveFilters<TFilters extends object>(
  filters: TFilters,
  exclusions: (keyof TFilters)[] = [],
  customCounters: Partial<Record<keyof TFilters, (value: unknown) => number>> = {}
): number {
  let count = 0;

  for (const key in filters) {
    // Skip excluded fields
    if (exclusions.includes(key as keyof TFilters)) {
      continue;
    }

    const value = filters[key];

    // Use custom counter if provided
    if (customCounters[key as keyof TFilters]) {
      const counter = customCounters[key as keyof TFilters]!;
      count += counter(value);
      continue;
    }

    // Default counting logic
    if (value === undefined || value === null || value === '') {
      continue;
    }

    // Array filters (e.g., categoryIds)
    if (Array.isArray(value)) {
      count += value.length;
      continue;
    }

    // String filters (non-empty)
    if (typeof value === 'string' && value.trim().length > 0) {
      count += 1;
      continue;
    }

    // Boolean filters (count if explicitly true or false, not null)
    if (typeof value === 'boolean') {
      count += 1;
      continue;
    }

    // Number filters (count if not 0)
    if (typeof value === 'number' && value !== 0) {
      count += 1;
      continue;
    }
  }

  return count;
}

/**
 * Main hook for list pages
 *
 * Provides common functionality for all list pages:
 * - Table props from entity-specific hook
 * - Loading and error states
 * - Active filter count calculation
 * - Refresh function
 *
 * Does NOT handle:
 * - Filter state management (stays in Zustand stores)
 * - Column definitions (stays in components)
 * - Entity-specific actions (stays in components)
 */
export function useListPage<TFilters extends object, TRecord = TableRecordType>({
  useTableHook,
  filters,
  filterCountExclusions = [],
  compactMode: _compactMode = false,
  customFilterCounters = {},
}: UseListPageOptions<TFilters, TRecord>): UseListPageReturn<TFilters, TRecord> {
  // Call the entity-specific table hook
  const { tableProps, isLoading, error, refresh } = useTableHook(filters);

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    return countActiveFilters(filters, filterCountExclusions, customFilterCounters);
  }, [filters, filterCountExclusions, customFilterCounters]);

  // Search term counts toward the empty vs zero-results split even when it is
  // excluded from the filter badge count.
  const searchValue = (filters as { search?: unknown }).search;
  const hasSearch = typeof searchValue === 'string' && searchValue.trim().length > 0;
  const isFiltered = hasSearch || activeFilterCount > 0;

  // Derive the list state triad ONCE — pages map it onto the shared
  // ListEmptyState / ListErrorState components from TableContainer.
  const dataCount = tableProps.dataSource?.length ?? 0;
  const listState: ListState = isLoading
    ? 'loading'
    : error
      ? 'error'
      : dataCount === 0
        ? isFiltered
          ? 'zero-results'
          : 'empty'
        : 'data';

  return {
    tableProps,
    isLoading,
    error: error ?? null,
    refresh,
    filters,
    activeFilterCount,
    isFiltered,
    listState,
  };
}

/**
 * Helper: Create custom counter for comma-separated country filter
 * Used by Vendor and Customer list pages
 */
export function createCountryCounter(value: unknown): number {
  if (typeof value !== 'string' || !value) return 0;
  return value.split(',').filter((loc) => loc.trim()).length;
}

/**
 * Helper: Create common inactive row class name
 */
export function getInactiveRowClassName<T extends { isActive?: boolean }>(record: T): string {
  return !record.isActive ? 'inactive-row' : '';
}
